<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import {
  Bug,
  Check,
  ChevronRight,
  DatabaseZap,
  LayoutDashboard,
  LogOut,
  Search,
  Sparkles,
  UserRound,
} from '@lucide/vue'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxRoot,
  ComboboxViewport,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  VisuallyHidden,
} from 'reka-ui'
import { computed, nextTick, onUnmounted, ref, shallowRef, toValue, watch } from 'vue'
import { toast } from 'vue-sonner'

import AppCommandPaletteIssueResults from '@/Components/AppCommandPaletteIssueResults.vue'
import { route } from '@/Composables/route'
import {
  prefetchVisit,
  useCommandPalette,
  type PaletteCommand,
} from '@/Composables/useCommandPalette'
import { useNavigation, usePermission } from '@/Composables/spaceship'
import { navigationIcon } from '@/lib/navigationIcons'
import { openBugReport } from '@/utils/bugReport'
import { logout } from '@/utils/logout'
import { resetLocalCache } from '@/utils/resetLocalCache'

import type { NavigationItem } from '@/Types'

interface CommandGroup {
  title: string
  commands: PaletteCommand[]
}

const navigation = useNavigation()
const { contextualActions, isOpen } = useCommandPalette()
const canSearchIssues = usePermission().hasAll('issues-read', 'issues-read-internal')
const query = ref('')
const selectedCommand = ref<PaletteCommand>()
const highlightedCommand = shallowRef<PaletteCommand>()
const comboboxRoot = ref<{
  highlightFirstItem?: () => void
  highlightItem?(value: unknown): void
} | null>(null)
const inputWrapper = ref<HTMLDivElement | null>(null)

const pageStack = shallowRef<PaletteCommand[]>([])
const currentPage = computed(() => pageStack.value.at(-1))
const pageCommands = computed(() =>
  currentPage.value?.children === undefined ? [] : toValue(currentPage.value.children),
)
const placeholder = computed(() =>
  currentPage.value === undefined ? 'Befehl oder Seite suchen …' : `${currentPage.value.title} …`,
)

useEventListener('keydown', (event: KeyboardEvent) => {
  // Browser autofill dispatches keydown events without `key`, despite the DOM type.
  if (event.key?.toLocaleLowerCase('de') !== 'k' || (!event.metaKey && !event.ctrlKey)) {
    return
  }

  event.preventDefault()
  isOpen.value = true
})

function toCommand(item: NavigationItem): PaletteCommand {
  return {
    id: item.url,
    title: item.title,
    perform: () => router.visit(item.url),
    onHighlight: prefetchVisit(item.url),
    icon: navigationIcon(item.attributes.icon),
    keywords: item.attributes.keywords?.split(' '),
  }
}

const commandGroups = computed<CommandGroup[]>(() => {
  const availableContextualActions = contextualActions.value.filter((action) => !action.hidden)
  const topLevelCommands = navigation.value
    .filter((item) => item.children.length === 0)
    .map((item) => toCommand(item))

  const childGroups = navigation.value
    .filter((item) => item.children.length > 0)
    .map((item) => ({
      title: item.title,
      commands: item.children.map(toCommand),
    }))

  return [
    ...(availableContextualActions.length > 0
      ? [
          {
            title: 'Aktionen',
            commands: availableContextualActions.map((action) => ({
              ...action,
              id: `context:${action.id}`,
              icon: action.icon ?? Sparkles,
            })),
          },
        ]
      : []),
    {
      title: 'Navigation',
      commands: [
        {
          id: 'dashboard',
          title: 'Dashboard',
          perform: () => router.visit(route('dashboard')),
          onHighlight: prefetchVisit(route('dashboard')),
          icon: LayoutDashboard,
          keywords: ['startseite', 'übersicht', 'home'],
        },
        ...topLevelCommands,
      ],
    },
    ...childGroups,
    {
      title: 'Konto',
      commands: [
        {
          id: 'profile',
          title: 'Profil',
          perform: () => router.visit(route('profile.show')),
          onHighlight: prefetchVisit(route('profile.show')),
          icon: UserRound,
          keywords: ['einstellungen', 'account'],
        },
        {
          id: 'logout',
          title: 'Abmelden',
          perform: () => logout(),
          icon: LogOut,
          keywords: ['logout', 'ausloggen'],
        },
      ],
    },
    {
      title: 'Hilfe',
      commands: [
        {
          id: 'bug-report',
          title: 'Bug melden',
          perform: openBugReport,
          icon: Bug,
          keywords: ['fehler', 'feedback', 'problem'],
        },
        {
          id: 'reset-local-cache',
          title: 'Lokalen Cache zurücksetzen',
          perform: resetLocalCache,
          icon: DatabaseZap,
          keywords: ['daten', 'neu laden', 'reset', 'electric'],
        },
      ],
    },
  ]
})

function commandSearchText(command: PaletteCommand, groupTitle = ''): string {
  return `${command.title} ${groupTitle} ${command.keywords?.join(' ') ?? ''}`
}

function closePalette(): void {
  isOpen.value = false
}

function setPaletteOpen(open: boolean): void {
  isOpen.value = open
}

async function highlightFirstItem(): Promise<void> {
  await nextTick()
  comboboxRoot.value?.highlightFirstItem?.()
}

function focusSearchInput(): void {
  inputWrapper.value?.querySelector('input')?.focus()
}

function enterPage(command: PaletteCommand): void {
  pageStack.value = [...pageStack.value, command]
  query.value = ''
  highlightedCommand.value = undefined
  focusSearchInput()
  void highlightFirstItem()
}

function popPage(): void {
  pageStack.value = pageStack.value.slice(0, -1)
  query.value = ''
  highlightedCommand.value = undefined
  void highlightFirstItem()
}

function runPerform(command: PaletteCommand): Promise<void> {
  return Promise.resolve()
    .then(() => command.perform?.())
    .catch((error: unknown) => {
      console.error(error)
      toast.error('Die Aktion konnte nicht ausgeführt werden.')
    })
}

function executeCommand(command: PaletteCommand | null | undefined): void {
  if (command === undefined || command === null) {
    return
  }

  selectedCommand.value = undefined

  if (command.children !== undefined) {
    enterPage(command)
    return
  }

  if (!command.toggle) {
    isOpen.value = false
  }

  void runPerform(command)
}

// Toggle items re-mount when their active state changes (their :key includes it, to escape
// reka's per-item render memo), which drops the highlight — so once the perform has run and
// the resulting re-render has flushed, re-highlight the item by id.
function toggleCommand(command: PaletteCommand): void {
  void runPerform(command)
    .then(() => nextTick())
    .then(() => comboboxRoot.value?.highlightItem?.(command))
}

function selectOriginalEvent(event: Event): Event | undefined {
  if (!(event instanceof CustomEvent)) {
    return undefined
  }

  const detail: unknown = event.detail
  if (
    detail !== null &&
    typeof detail === 'object' &&
    'originalEvent' in detail &&
    detail.originalEvent instanceof Event
  ) {
    return detail.originalEvent
  }

  return undefined
}

// Selecting an item normally makes reka close the listbox before it updates the model value,
// so drill-in and toggle commands preventDefault the select event (reka then skips both) and
// are handled directly. On a toggle, a pointer click toggles and stays open while Enter
// confirms: it activates the item if it isn't active yet, then closes the palette. reka
// translates Enter into a synthetic click on the highlighted item, so real pointer clicks are
// detected via the click detail (0 for synthetic clicks, ≥ 1 for real ones).
function onItemSelect(event: Event, command: PaletteCommand): void {
  if (command.children !== undefined) {
    event.preventDefault()
    executeCommand(command)
    return
  }

  if (!command.toggle) {
    return
  }

  event.preventDefault()

  const originalEvent = selectOriginalEvent(event)
  if (originalEvent instanceof MouseEvent && originalEvent.detail > 0) {
    toggleCommand(command)
    focusSearchInput()
    return
  }

  if (!command.active) {
    void runPerform(command)
  }
  isOpen.value = false
}

function onInputSpace(event: KeyboardEvent): void {
  const command = highlightedCommand.value
  if (command?.toggle === true) {
    event.preventDefault()
    toggleCommand(command)
  }
}

function onInputEscape(): void {
  if (currentPage.value === undefined) {
    closePalette()
    return
  }

  popPage()
}

function onInputBackspace(event: KeyboardEvent): void {
  if (query.value.length === 0 && currentPage.value !== undefined) {
    event.preventDefault()
    popPage()
  }
}

const notifyHighlightedCommand = useDebounceFn((command: PaletteCommand) => {
  command.onHighlight?.()
}, 200)

function onHighlight(payload: { ref: HTMLElement; value: PaletteCommand } | undefined): void {
  highlightedCommand.value = payload?.value
  if (payload !== undefined) {
    void notifyHighlightedCommand(payload.value)
  }
}

const removeNavigateListener = router.on('navigate', () => {
  isOpen.value = false
})

onUnmounted(removeNavigateListener)

function emptyDisplayValue(): string {
  return ''
}

watch(isOpen, (open) => {
  if (open) {
    return
  }

  query.value = ''
  selectedCommand.value = undefined
  highlightedCommand.value = undefined
  pageStack.value = []
})
</script>

<template>
  <DialogRoot :open="isOpen" @update:open="setPaletteOpen">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-50 bg-zinc-950/20" />

      <DialogContent
        class="fixed top-[8vh] left-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl outline-none dark:border-zinc-700 dark:bg-zinc-900"
      >
        <VisuallyHidden>
          <DialogTitle>Befehlspalette</DialogTitle>
          <DialogDescription>Eine Aktion ausführen oder zu einer Seite wechseln.</DialogDescription>
        </VisuallyHidden>

        <ComboboxRoot
          ref="comboboxRoot"
          :model-value="selectedCommand"
          :open="isOpen"
          by="id"
          class="flex max-h-[min(75dvh,42rem)] flex-col"
          @update:model-value="executeCommand"
          @update:open="setPaletteOpen"
          @highlight="onHighlight"
        >
          <div class="shrink-0 border-b border-zinc-100 dark:border-zinc-800">
            <div v-if="pageStack.length > 0" class="flex flex-wrap items-center gap-1.5 px-4 pt-3">
              <span
                v-for="page in pageStack"
                :key="page.id"
                class="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
              >
                {{ page.title }}
              </span>
            </div>
            <div ref="inputWrapper" class="relative">
              <Search
                class="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-zinc-400"
                aria-hidden="true"
              />
              <ComboboxInput
                v-model="query"
                auto-focus
                :display-value="emptyDisplayValue"
                :placeholder="placeholder"
                class="h-14 w-full border-0 bg-transparent pr-16 pl-11 text-base text-zinc-900 placeholder:text-zinc-400 focus:ring-0 focus:outline-none dark:text-zinc-100"
                @keydown.esc.prevent="onInputEscape"
                @keydown.backspace="onInputBackspace"
                @keydown.space="onInputSpace"
              />
              <kbd
                class="absolute top-1/2 right-4 -translate-y-1/2 rounded-md border border-zinc-200 px-1.5 py-0.5 text-xs font-medium text-zinc-400 dark:border-zinc-700"
              >
                Esc
              </kbd>
            </div>
          </div>

          <ComboboxContent class="min-h-0 flex-1 overflow-hidden">
            <ComboboxViewport class="max-h-[min(65dvh,36rem)] scroll-py-2 p-2">
              <ComboboxEmpty class="px-3 py-10 text-center text-sm text-zinc-400">
                Keine Treffer.
              </ComboboxEmpty>

              <template v-if="currentPage === undefined">
                <ComboboxGroup v-for="group in commandGroups" :key="group.title">
                  <ComboboxLabel
                    class="px-3 pt-3 pb-1.5 text-xs font-medium text-zinc-400 first:pt-1 dark:text-zinc-500"
                  >
                    {{ group.title }}
                  </ComboboxLabel>

                  <ComboboxItem
                    v-for="command in group.commands"
                    :key="command.id"
                    :value="command"
                    :text-value="commandSearchText(command, group.title)"
                    class="group flex cursor-default items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-zinc-700 outline-none data-highlighted:bg-zinc-100 data-highlighted:text-zinc-950 dark:text-zinc-300 dark:data-highlighted:bg-zinc-800 dark:data-highlighted:text-white"
                    @select="(event) => onItemSelect(event, command)"
                  >
                    <Component
                      :is="command.icon ?? Sparkles"
                      class="size-4 shrink-0 text-zinc-400 group-data-highlighted:text-zinc-600 dark:group-data-highlighted:text-zinc-300"
                      aria-hidden="true"
                    />
                    <span class="min-w-0 flex-1 truncate">{{ command.title }}</span>
                    <ChevronRight
                      v-if="command.children"
                      class="size-4 shrink-0 text-zinc-400"
                      aria-hidden="true"
                    />
                    <kbd
                      v-else-if="command.hint"
                      class="rounded-md border border-zinc-200 px-1.5 py-0.5 text-xs font-medium text-zinc-400 group-data-highlighted:border-zinc-300 dark:border-zinc-700 dark:group-data-highlighted:border-zinc-600"
                    >
                      {{ command.hint }}
                    </kbd>
                  </ComboboxItem>
                </ComboboxGroup>

                <AppCommandPaletteIssueResults v-if="canSearchIssues" :query="query" />
              </template>

              <template v-else>
                <ComboboxItem
                  v-for="command in pageCommands"
                  :key="`${command.id}:${command.active === true}`"
                  :value="command"
                  :text-value="commandSearchText(command)"
                  :aria-checked="command.toggle ? command.active === true : undefined"
                  class="group flex cursor-default items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-zinc-700 outline-none data-highlighted:bg-zinc-100 data-highlighted:text-zinc-950 dark:text-zinc-300 dark:data-highlighted:bg-zinc-800 dark:data-highlighted:text-white"
                  @select="(event) => onItemSelect(event, command)"
                >
                  <span
                    v-if="command.toggle"
                    class="flex size-4 shrink-0 items-center justify-center rounded border transition"
                    :class="
                      command.active
                        ? 'border-sky-500 bg-sky-50 dark:border-sky-500 dark:bg-sky-950/50'
                        : 'border-zinc-300 group-data-highlighted:border-zinc-400 dark:border-zinc-600 dark:group-data-highlighted:border-zinc-500'
                    "
                    aria-hidden="true"
                  >
                    <Check v-if="command.active" class="size-3 text-sky-600 dark:text-sky-400" />
                  </span>
                  <span
                    v-else
                    class="flex size-4 shrink-0 items-center justify-center"
                    aria-hidden="true"
                  >
                    <Component
                      :is="command.icon"
                      v-if="command.icon"
                      class="size-4 text-zinc-400 group-data-highlighted:text-zinc-600 dark:group-data-highlighted:text-zinc-300"
                    />
                  </span>
                  <span class="min-w-0 flex-1 truncate">{{ command.title }}</span>
                  <ChevronRight
                    v-if="command.children"
                    class="size-4 shrink-0 text-zinc-400"
                    aria-hidden="true"
                  />
                  <kbd
                    v-else-if="command.hint"
                    class="rounded-md border border-zinc-200 px-1.5 py-0.5 text-xs font-medium text-zinc-400 group-data-highlighted:border-zinc-300 dark:border-zinc-700 dark:group-data-highlighted:border-zinc-600"
                  >
                    {{ command.hint }}
                  </kbd>
                </ComboboxItem>
              </template>
            </ComboboxViewport>
          </ComboboxContent>
        </ComboboxRoot>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
